/**
 * AgentsList — Tab de Agentes (tabela estilo painel admin Big Tech)
 * Colunas: Avatar | Nome | LLM | Função | Canais | Integrações | Ações
 */
import { Button, Icon } from '../../components/ui';
import { T } from '../../styles/tokens';
import { AgentRow } from './AgentCard';
import type { Environment } from './types';

interface AgentsListProps {
  environments: Environment[];
  userPlan?: string;
  loading?: boolean;
  onCreateAgent: () => void;
  tokenUsage?: { today: { total: number; requests: number }; month: { total: number; requests: number } } | null;
}

/** Header de coluna */
function ColHeader({ children, flex, align }: { children: string; flex: number; align?: string }) {
  return (
    <div style={{
      flex, minWidth: 0, paddingRight: 12,
      fontSize: 10, fontWeight: 700, color: T.textSecondary,
      textTransform: 'uppercase' as const, letterSpacing: '0.8px',
      textAlign: (align as any) || 'left',
    }}>
      {children}
    </div>
  );
}

export function AgentsList({ environments, userPlan, loading, onCreateAgent, tokenUsage }: AgentsListProps) {
  if (loading) {
    return (
      <div style={{ borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        {[1, 2, 3].map(i => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
            borderBottom: `1px solid ${T.border}`,
          }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.bgHover, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ width: '35%', height: 12, background: T.bgHover, borderRadius: 4, marginBottom: 6 }} />
              <div style={{ width: '55%', height: 10, background: T.bgHover, borderRadius: 4 }} />
            </div>
            <div style={{ width: '15%', height: 10, background: T.bgHover, borderRadius: 4 }} />
            <div style={{ width: '10%', height: 10, background: T.bgHover, borderRadius: 4 }} />
          </div>
        ))}
      </div>
    );
  }

  // Extrair todos os agentes — primeiro de cada máquina = main
  const allAgents = environments.flatMap(env => {
    const agents = env.agents && env.agents.length > 0
      ? env.agents
      : env.agent_info ? [env.agent_info] : [];
    // Se não tem agentes mas o ambiente existe (criando/provisionando), mostrar placeholder
    if (agents.length === 0) {
      return [{ agent: { agent_name: env.agent_name || env.name || 'Novo agente', agent_model: env.agent_model || null, agent_brain: null, brain_name: null, channels: [], tools: [], skills: [] }, env, isMain: true }];
    }
    return agents.map((a, idx) => ({ agent: a, env, isMain: idx === 0 }));
  });
  // Main primeiro
  allAgents.sort((a, b) => (a.isMain === b.isMain ? 0 : a.isMain ? -1 : 1));

  if (allAgents.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '80px 24px', gap: 12,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>Nenhum agente criado</div>
        <div style={{ fontSize: 13, color: T.textSecondary, textAlign: 'center', maxWidth: 360, marginBottom: 16 }}>
          Crie seu primeiro agente para começar
        </div>
        <Button variant="ghost" size="sm" onClick={onCreateAgent} icon={<Icon name="plus" size={14} />} style={{ borderRadius: 20 }}>
          Criar Agente
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden',
      background: T.bgCard, marginBottom: 32,
    }}>
      {/* Header da tabela — desktop only */}
      <div class="agent-header-desktop" style={{
        display: 'flex', alignItems: 'center', gap: 16,
        padding: '10px 20px',
        borderBottom: `1px solid ${T.border}`,
        background: T.bgSubtle || T.bgInput,
      }}>
        <div style={{ width: 32, flexShrink: 0 }} />
        <ColHeader flex={2.5}>Agente</ColHeader>
        <ColHeader flex={0.8}>Canais</ColHeader>
        <ColHeader flex={0.8}>Integrações</ColHeader>
        <ColHeader flex={1.2} align="right">Uso</ColHeader>
        <div style={{ width: 76, flexShrink: 0 }} />
      </div>

      {/* Rows */}
      {allAgents.map(({ agent, env, isMain }, idx) => (
        <AgentRow
          key={`${env.id}-${agent.id || idx}`}
          agent={agent}
          env={env}
          userPlan={userPlan}
          isMain={isMain}
        />
      ))}
    </div>
  );
}
